// @flow

// Responsible for managing the different states of the CC mode window.
//
// Currently, just the conversation view is available. With the new CC
// navigation design, there will be two other states while staying in this CC
// mode window:
//  - Rating: Displays a rating section for the user to rate the conversation
//  - Home: Displays a button to quickly start a conversation or close the app

import React, { useEffect } from 'react';

import ConversationView from '../ConversationView';
import HomeView from '../HomeView';
import RatingView from '../RatingView';

import type { CCMode } from '../../../types/cc-mode';
import { isLinux, isWindows } from '../../../utils';
import { getCCModeDimensions, getCCModePosition } from '../../../utils/conversation';

import useStyles from './CCPage.styles';
import { ipcRendererWithDeregistering } from '../../../utils/electron';

interface Props {
  appSubmittedTypeform: Object;
  ccMode: CCMode;
  createNewConversation: () => Promise<void>;
  firebaseUser: Object;
  isInviteCardOpen: boolean;
  onEndConversation: () => void;
  roomEndingWarningCountdown: number;
  roomEndingWarningBegin: number;
  roomEndingWarningProlongUrl: string;
  setCCMode: (mode: CCMode) => void;
  setInviteCardOpen: (isOpen: boolean) => void;
  setShowBubbleAboveCCMode: (shouldShow: boolean) => void;
  showBubbleAboveCCMode: boolean;
  user: Object;
}

const CCPage = ({
  appSubmittedTypeform,
  ccMode,
  createNewConversation,
  firebaseUser,
  isInviteCardOpen,
  onEndConversation,
  roomEndingWarningCountdown,
  roomEndingWarningBegin,
  roomEndingWarningProlongUrl,
  setCCMode,
  setInviteCardOpen,
  setShowBubbleAboveCCMode,
  showBubbleAboveCCMode,
}: Props) => {
  const classes = useStyles();

  // Mac users can close the application and still be open in the background.
  // If the user closed the application from CC widget mode, then opening the
  // app again will start a new conversation immediately.
  useEffect(() => {
    const goInFloatingModeAndAttemptStartConversation = () => {
      window.electronIPC.sendGoInFloatingMode();
      if (firebaseUser && !firebaseUser.isAnonymous) {
        setCCMode('conversation');
        createNewConversation();
      }
    };

    if (window.isElectron) {
      ipcRendererWithDeregistering.addEventListener('window-activated', goInFloatingModeAndAttemptStartConversation);
    }

    return () => {
      if (window.isElectron) {
        ipcRendererWithDeregistering.removeEventListener(
          'window-activated',
          goInFloatingModeAndAttemptStartConversation
        );
      }
    };
  }, []);

  useEffect(() => {
    if (ccMode === 'conversation') {
      const { width, height } = getCCModeDimensions();
      const { x, y } = getCCModePosition();
      window.electronIPC.sendCCMode(ccMode, width, height, x, y);
    } else {
      window.electronIPC.sendCCMode(ccMode);
    }
    setShowBubbleAboveCCMode(false);
  }, [ccMode]);

  // Electron has a limitation of not being able to click through a transparent
  // window. The workaround is to directly handle ignoring mouse events to allow
  // clicks to pass through the window. If the invisible items are hidden, we
  // know to ignore mouse events.
  //
  // This is not done for Windows machine because when entering a draggable
  // region, all pointer events are lost. This would cause showBubbleAboveCCMode
  // to become false and ignore mouse events when it shouldn't have.
  useEffect(() => {
    const currentWindow = window.electronCurrentWindow;
    if (!isLinux && !isWindows) {
      if (showBubbleAboveCCMode) {
        currentWindow.setIgnoreMouseEvents(false);
      } else {
        currentWindow.setIgnoreMouseEvents(true, { forward: true });
        if (isInviteCardOpen) {
          setInviteCardOpen(false);
        }
      }
    }
  }, [showBubbleAboveCCMode]);

  return (
    <div
      className={classes.root}
      onMouseEnter={() => {
        if (isWindows || isLinux) setShowBubbleAboveCCMode(true);
      }}
      onMouseLeave={() => setShowBubbleAboveCCMode(false)}
    >
      {ccMode === 'home' && <HomeView />}
      {ccMode === 'conversation' && (
        <ConversationView
          onEndConversation={onEndConversation}
          roomEndingWarningCountdown={roomEndingWarningCountdown}
          roomEndingWarningBegin={roomEndingWarningBegin}
          roomEndingWarningProlongUrl={roomEndingWarningProlongUrl}
          appSubmittedTypeform={appSubmittedTypeform}
        />
      )}
      {ccMode === 'rating' && <RatingView />}
    </div>
  );
};

export default CCPage;
